// Shared game logic for Wordsheet
// Single source of truth for turn/move eligibility

// ============================================================================
// Constants
// ============================================================================

export const MOVE_REASON = {
    TURN: 'turn',           // It's their turn in normal rotation
    BEHIND: 'behind',       // Player is behind on turns (catch-up mode)
    RETURNING: 'returning', // Away player returning to game
    LAST: 'last',           // Last player standing (all others finished)
    WAITING: 'waiting',     // Blocked - waiting for others
    INACTIVE: 'inactive'    // Player is not active (left or finished)
};

// ============================================================================
// Debug Logging
// ============================================================================

// Debug flag - set via localStorage (browser) or environment variable (Node)
const DEBUG = typeof window !== 'undefined'
    ? window.localStorage?.getItem('DEBUG_GAME') === 'true'
    : typeof process !== 'undefined' && process.env?.DEBUG === 'true';

export function debugLog(...args) {
    if (DEBUG) {
        console.log('[GameLogic]', ...args);
    }
}

// ============================================================================
// Player State Helpers
// ============================================================================

/**
 * Check if a player is active (in game and not finished)
 * INVARIANTS:
 * - isFinished implies !isActive (finished players are not active)
 * - isIdle is only meaningful when isActive is true
 * @param {Object} player - Player object
 * @returns {boolean} True if player is active
 */
export function isPlayerActive(player) {
    return !!(player && player.isActive && !player.isFinished);
}

/**
 * Get the number of turns a player has taken
 * Handles both turnsTaken and legacy wordsPlayed fields
 * @param {Object} player - Player object
 * @returns {number} Number of turns taken
 */
export function getTurnsTaken(player) {
    if (!player) return 0;
    if (typeof player.turnsTaken === 'number') return player.turnsTaken;
    if (typeof player.wordsPlayed === 'number') return player.wordsPlayed;
    return 0;
}

// ============================================================================
// Core State Machine
// ============================================================================

/**
 * Determine if a player can make a move and why
 *
 * A player can move if ANY of these are true:
 * 1. They're behind on turns (catch-up mode)
 * 2. They're idle and returning (can play to rejoin)
 * 3. It's their turn in normal rotation
 * 4. They're the last player standing (everyone else finished/left)
 *
 * @param {Array} players - All players in the game
 * @param {number} currentTurnIndex - Index of current turn player
 * @param {string} playerId - ID of player to check
 * @returns {{canMove: boolean, reason: string}} Result with reason
 */
export function canPlayerMove(players, currentTurnIndex, playerId) {
    // Guard against bad input
    if (!players || players.length === 0) {
        return { canMove: false, reason: MOVE_REASON.INACTIVE };
    }
    if (currentTurnIndex < 0 || currentTurnIndex >= players.length) {
        currentTurnIndex = 0;
    }

    const player = players.find(p => p.id === playerId);

    // Must be active (in game and not finished)
    if (!player || !isPlayerActive(player)) {
        return { canMove: false, reason: MOVE_REASON.INACTIVE };
    }

    const playerTurns = getTurnsTaken(player);
    const currentPlayer = players[currentTurnIndex];
    const currentPlayerTurns = currentPlayer ? getTurnsTaken(currentPlayer) : 0;

    debugLog('canPlayerMove:', {
        playerId,
        playerTurns,
        currentPlayerTurns,
        isIdle: player.isIdle,
        currentTurnIndex
    });

    // === RULE 1: Is it my turn? (check first - turn advances depend on this) ===
    const isMyTurn = currentPlayer?.id === playerId && isPlayerActive(currentPlayer);
    if (isMyTurn) {
        // Block if all other active players are idle AND we're ahead on turns.
        // This prevents one player from playing infinitely while others are away,
        // but still allows a returning player to take the turn they're owed.
        const otherActivePlayers = players.filter(p => p.id !== playerId && isPlayerActive(p));
        const allOthersIdle = otherActivePlayers.length > 0 && otherActivePlayers.every(p => p.isIdle);
        if (allOthersIdle) {
            const aheadOfAll = otherActivePlayers.every(p => playerTurns > getTurnsTaken(p));
            if (aheadOfAll) {
                debugLog('  -> WAITING (ahead of all idle players)');
                return { canMove: false, reason: MOVE_REASON.WAITING };
            }
        }
        debugLog('  -> TURN (can move)');
        return { canMove: true, reason: MOVE_REASON.TURN };
    }

    // === RULE 2: Behind players can catch up (out of turn order) ===
    // Only behind if you have fewer turns than the current turn player
    // This prevents players at the same turn count from jumping ahead
    if (playerTurns < currentPlayerTurns) {
        debugLog('  -> BEHIND (can move)');
        return { canMove: true, reason: MOVE_REASON.BEHIND };
    }

    // === RULE 3: Idle player returning ===
    // An Away player can always play to rejoin (clears idle status on move)
    if (player.isIdle) {
        debugLog('  -> RETURNING (can move)');
        return { canMove: true, reason: MOVE_REASON.RETURNING };
    }

    // === RULE 4: Am I the last player standing? ===
    // "Last player" means no one else COULD play (active and not finished)
    // Fix from review: exclude players who LEFT (!isActive), not just finished
    const othersWhoCouldPlay = players.filter(p =>
        p.id !== playerId && isPlayerActive(p)
    );
    if (othersWhoCouldPlay.length === 0) {
        debugLog('  -> LAST (can move)');
        return { canMove: true, reason: MOVE_REASON.LAST };
    }

    // At max turns, not my turn, others could still play/return
    debugLog('  -> WAITING (blocked)');
    return { canMove: false, reason: MOVE_REASON.WAITING };
}

// ============================================================================
// Turn Advancement
// ============================================================================

/**
 * Find the next player who should receive the turn
 *
 * Rules:
 * - Skip inactive players (left or finished)
 * - Skip idle players if at least one active non-idle player remains
 * - If all remaining players are idle, include them in rotation
 *
 * @param {Array} players - All players in the game
 * @param {number} currentIdx - Current turn index
 * @param {number|null} excludeIdx - Player index to exclude from counting (the one who just acted)
 * @returns {{nextIndex: number, isIdlePlayer: boolean}} Next turn index and whether they're idle
 */
export function findNextTurnIndex(players, currentIdx, excludeIdx = null) {
    // Guard against bad input
    if (!players || players.length === 0) return { nextIndex: 0, isIdlePlayer: false };
    if (currentIdx < 0 || currentIdx >= players.length) currentIdx = 0;

    const numPlayers = players.length;

    // 1. Count active non-idle players (excluding the one who just acted)
    const activeNonIdle = players.filter((p, i) =>
        isPlayerActive(p) && !p.isIdle && i !== excludeIdx
    );

    // 2. Skip idle players only if others remain
    const skipIdle = activeNonIdle.length >= 1;

    debugLog('findNextTurnIndex:', {
        currentIdx,
        excludeIdx,
        activeNonIdleCount: activeNonIdle.length,
        skipIdle
    });

    // 3. Find next valid player
    for (let i = 1; i <= numPlayers; i++) {
        const idx = (currentIdx + i) % numPlayers;
        const p = players[idx];

        if (!isPlayerActive(p)) continue;
        if (skipIdle && p.isIdle) continue;

        debugLog('  -> nextIndex:', idx, 'isIdle:', !!p.isIdle);
        return { nextIndex: idx, isIdlePlayer: !!p.isIdle };
    }

    debugLog('  -> no change, staying at:', currentIdx);
    return { nextIndex: currentIdx, isIdlePlayer: false };
}

// ============================================================================
// Player Factory
// ============================================================================

/**
 * Create a new player object with all required fields
 * Ensures consistency and prevents missing field errors (#13)
 *
 * @param {string} id - Player ID (Firebase UID or Discord ID)
 * @param {string} nickname - Display name
 * @param {Object} options - Optional overrides
 * @returns {Object} Player object with all fields initialized
 */
export function createPlayer(id, nickname, options = {}) {
    return {
        id,
        nickname,
        score: 0,
        wordsPlayed: 0,
        turnsTaken: 0,
        rack: [],
        isActive: true,
        isIdle: false,
        isFinished: false,
        lastActiveAt: Date.now(),
        ...options
    };
}

// ============================================================================
// UI Helpers
// ============================================================================

/**
 * Get user-friendly message for a move reason
 * Used by both web and Discord clients for consistent UI
 *
 * @param {string} reason - MOVE_REASON value
 * @returns {string|null} Message to display, or null for inactive
 */
export function getReasonMessage(reason) {
    const messages = {
        [MOVE_REASON.TURN]: "Your turn!",
        [MOVE_REASON.BEHIND]: "You're behind - play to catch up!",
        [MOVE_REASON.RETURNING]: "You're away - play to rejoin",
        [MOVE_REASON.LAST]: "Last player standing - finish when ready",
        [MOVE_REASON.WAITING]: "Waiting for others to play or return...",
        [MOVE_REASON.INACTIVE]: null
    };
    return messages[reason] || null;
}

// ============================================================================
// Wheel of Fortune (late-join bonus)
// ============================================================================

/**
 * 20 wheel segments with values distributed so high values aren't clustered.
 * Probability: 10 pts (7/20 = 35%) | 20 pts (6/20 = 30%) | 25 pts (4/20 = 20%)
 *            | 50 pts (2/20 = 10%) | 100 pts (1/20 = 5%)
 */
export const WHEEL_SEGMENTS = [
    10, 20, 10, 25, 10, 20, 50, 10, 20, 25,
    10, 20, 100, 25, 10, 20, 10, 25, 20, 50
];

/**
 * Spin the wheel of fortune for a late-joining player.
 * Weighted random selection from WHEEL_SEGMENTS.
 * @returns {number} Bonus points awarded
 */
export function spinWheelOfFortune() {
    const index = Math.floor(Math.random() * WHEEL_SEGMENTS.length);
    return WHEEL_SEGMENTS[index];
}

/**
 * Check if a reason means the player can move
 * @param {string} reason - MOVE_REASON value
 * @returns {boolean} True if player can move with this reason
 */
export function canMoveWithReason(reason) {
    return reason === MOVE_REASON.TURN ||
           reason === MOVE_REASON.BEHIND ||
           reason === MOVE_REASON.RETURNING ||
           reason === MOVE_REASON.LAST;
}
